<template>
  <component
    :is="selectWrapperTag"
    class="select"
    @click="onWrapperClick"
  >
    <select
      ref="selectEle"
      :style="selectNativeStyle"
      v-model="curValue"
      :disabled="disabled"
      :class="[
        {
          'placeholder-style': value == undefined || disabled
        },
        pointClass
      ]"
      @change="onSelect($event.target.value)"
    >
      <option v-if="placeholder" disabled selected :value="undefined">
        {{ placeholder }}
      </option>
      <slot></slot>
    </select>
    <img
      v-if="arrowImage"
      class="select-arrow select-arrow--image"
      :src="arrowImage"
      alt=""
    />
    <van-icon
      v-else
      class="select-arrow"
      :class="{
        'placeholder-style': disabled
      }"
      name="arrow-down"
    />
  </component>
</template>

<script>
export default {
  name: 'Select',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: [String, Number],
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    pointClass: {
      type: String,
      default: ''
    },
    /** 语言选择等场景：自定义下拉箭头图，替代 van-icon */
    arrowImage: {
      type: String,
      default: ''
    },
    /** lang：客服 LangChoose，避免 .select 默认 width/height 100%/44px 撑乱 pill */
    layout: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      curValue: undefined,
      /** PNS 竖屏不用 label 包 select，否则 WebKit 会算出背景却不绘制 */
      isPortrait: this.readIsPortrait()
    };
  },
  watch: {
    value(val) {
      if (val == this.curValue) return;
      this.curValue = val;
    }
  },
  computed: {
    /**
     * 仅 PNS（有箭头图）横屏保留 label，让系统把点击交给 select。
     * 竖屏改为 div：label 内的背景和文字在 WebKit 里经常不画出来。
     */
    selectWrapperTag() {
      if (this.arrowImage && !this.isPortrait) return 'label';
      return 'div';
    },
    isLangLayout() {
      return this.layout === 'lang' || !!this.arrowImage;
    },
    selectNativeStyle() {
      return {
        float: this.isLangLayout ? 'none' : 'left',
        zIndex: 0,
        position: 'relative',
        width: this.isLangLayout ? 'auto' : '100%',
        height: '100%',
        flex: this.isLangLayout ? '0 1 auto' : undefined,
        backgroundColor: 'transparent',
        color: 'inherit',
        outline: 'inherit',
        border: 0,
        fontSize: 'inherit',
        cursor: 'pointer'
      };
    }
  },
  created() {
    this.curValue = this.value;
  },
  methods: {
    readIsPortrait() {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return window.matchMedia('(orientation: portrait)').matches;
    },
    syncPortrait() {
      this.isPortrait = this.readIsPortrait();
    },
    /** div 外壳没有 label 的关联点击，把箭头和内边距上的点击交给 select */
    onWrapperClick(e) {
      if (this.selectWrapperTag === 'label') return;
      const selectEle = this.$refs.selectEle;
      if (!selectEle || selectEle.disabled) return;
      if (e.target === selectEle) return;
      if (typeof selectEle.click === 'function') {
        selectEle.click();
      }
    },
    onSelect(value) {
      this.$emit('change', value);
      const selectEle = this.$refs.selectEle;
      selectEle && typeof selectEle.blur === 'function' && selectEle.blur();
    },
    // 这里和上面的blur，主要是为了避免在选中select的时候切换屏幕再回来时会自动触发聚焦上报。
    selectAutoBlur() {
      if (document?.visibilityState == 'hidden') {
        try {
          const selectElem = this.$refs.selectEle;
          selectElem &&
            typeof selectElem.blur === 'function' &&
            selectElem.blur();
        } catch (error) {
          console.error(error);
        }
      }
    }
  },
  mounted() {
    this.portraitMedia = window.matchMedia('(orientation: portrait)');
    this.syncPortrait();
    if (this.portraitMedia.addEventListener) {
      this.portraitMedia.addEventListener('change', this.syncPortrait);
    } else if (this.portraitMedia.addListener) {
      this.portraitMedia.addListener(this.syncPortrait);
    }
    document.addEventListener('visibilitychange', this.selectAutoBlur);
  },
  beforeDestroy() {
    if (this.portraitMedia) {
      if (this.portraitMedia.removeEventListener) {
        this.portraitMedia.removeEventListener('change', this.syncPortrait);
      } else if (this.portraitMedia.removeListener) {
        this.portraitMedia.removeListener(this.syncPortrait);
      }
    }
    document.removeEventListener('visibilitychange', this.selectAutoBlur);
  }
};
</script>

<style lang="scss">
.select {
  display: flex;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  // padding: 0 62px 0 20px;
  background-color: var(--block-bg-light-color);
  border-radius: var(--border-radius);
  font-size: inherit;
  color: var(--text-color);

  > select {
    // iOS原生select自带padding
    padding: 0;
    // 去除原生下拉箭头
    -webkit-appearance: none; /* Safari 和 Chrome */
    -moz-appearance: none; /* Firefox */
    appearance: none; /* 其他浏览器 */
    &:disabled {
      color: var(--placeholder-color) !important;
      -webkit-text-fill-color: var(--placeholder-color) !important;
    }
  }

  .placeholder-style {
    color: var(--placeholder-color) !important;
  }

  &.lang-choose-select {
    width: auto;
    align-items: center;
  }

  &-arrow {
    z-index: 0;
    position: relative;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;
    font-weight: bold;
    color: var(--back-color) !important;

    &--image {
      display: block;
      margin: 0;
    }
  }

  &.lang-choose-select .select-arrow {
    margin: 0;
    align-self: center;
  }
}
</style>
